import React from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import {Box, Colors, IconChevronUp, Text} from "@bounce/shared/ui";
import {Button} from "../../../ui/components";
import {ParamSchema} from "../../../apis/requests/segments/types";
import ConditionGroupCard from "./ConditionGroupCard";
import {makeDefaultCondition} from "./helpers";
import {ConditionsFormValues} from "./schema";

type Props = {
  parameters: ParamSchema[];
  description?: string;
};

const DEFAULT_DESCRIPTION = "Define the rules that determine segment membership";

const BRACKET_ZONE_WIDTH = 56;
const SEAM_ROW_HEIGHT = 42;

const ConnectorSegment: React.FC<{isFirst: boolean; isLast: boolean; highlighted: boolean}> = ({
  isFirst,
  isLast,
  highlighted,
}) => {
  const color = highlighted ? Colors.blue._300 : Colors.tertiary._400;
  return (
    <Box sx={{position: "relative", width: BRACKET_ZONE_WIDTH, flexShrink: 0, alignSelf: "stretch"}}>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: isFirst ? "50%" : 0,
          bottom: isLast ? "50%" : 0,
          width: 2,
          backgroundColor: color,
          transform: "translateX(-50%)",
          transition: "background-color 0.2s ease",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          right: 0,
          top: "50%",
          height: 2,
          backgroundColor: color,
          transition: "background-color 0.2s ease",
        }}
      />
    </Box>
  );
};

const SeamToggle: React.FC<{
  value: "all" | "any";
  onToggle: () => void;
  highlighted: boolean;
  onHover: (h: boolean) => void;
}> = ({value, onToggle, highlighted, onHover}) => {
  const color = highlighted ? Colors.blue._300 : Colors.tertiary._400;
  return (
    <Box sx={{display: "flex", height: SEAM_ROW_HEIGHT}}>
      <Box
        sx={{
          width: BRACKET_ZONE_WIDTH,
          flexShrink: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: color,
            transform: "translateX(-50%)",
            transition: "background-color 0.2s ease",
          }}
        />
        <Box
          onClick={onToggle}
          onMouseEnter={() => onHover(true)}
          onMouseLeave={() => onHover(false)}
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            width: 52,
            height: 26,
            borderRadius: "13px",
            border: `1.5px solid ${highlighted ? Colors.blue._400 : Colors.tertiary._400}`,
            backgroundColor: highlighted ? Colors.blue._100 : Colors.bw.white,
            cursor: "pointer",
            userSelect: "none",
            transition: "all 0.2s ease",
          }}
        >
          <Text
            size={11}
            variant="inter600"
            sx={{
              color: highlighted ? Colors.blue._700 : Colors.grey._600,
              letterSpacing: "0.5px",
              transition: "color 0.2s ease",
            }}
          >
            {value === "all" ? "AND" : "OR"}
          </Text>
          <IconChevronUp
            width={10}
            height={10}
            color={highlighted ? Colors.blue._500 : Colors.grey._400}
            style={{transform: "rotate(180deg)", transition: "color 0.2s ease"}}
          />
        </Box>
      </Box>
    </Box>
  );
};

const ConditionsCard: React.FC<Props> = ({parameters, description = DEFAULT_DESCRIPTION}) => {
  const {control, watch, setValue} = useFormContext<ConditionsFormValues>();
  const {fields, append, remove} = useFieldArray({control, name: "groups"});
  const groupMatchMode = watch("group_match_mode");
  const [connectorHover, setConnectorHover] = React.useState(false);
  const hasMultiple = fields.length > 1;

  const toggleGroupMatch = () =>
    setValue("group_match_mode", groupMatchMode === "all" ? "any" : "all", {shouldDirty: true});

  const onAddGroup = () => append({name: "", match_mode: "all", conditions: [makeDefaultCondition(parameters)]});

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: Colors.tertiary._100,
        borderRadius: "12px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box>
        <Text variant="interDisplay600" size={16} sx={{color: Colors.grey._800}}>
          Conditions
        </Text>
        <Text size={13} sx={{color: Colors.grey._500, mt: 0.25}}>
          {description}
        </Text>
      </Box>

      <Box sx={{display: "flex", flexDirection: "column"}}>
        {fields.map((field, groupIndex) => (
          <React.Fragment key={field.id}>
            {groupIndex > 0 && (
              <SeamToggle
                value={groupMatchMode}
                onToggle={toggleGroupMatch}
                highlighted={connectorHover}
                onHover={setConnectorHover}
              />
            )}
            <Box sx={{display: "flex"}}>
              {hasMultiple && (
                <ConnectorSegment
                  isFirst={groupIndex === 0}
                  isLast={groupIndex === fields.length - 1}
                  highlighted={connectorHover}
                />
              )}
              <Box sx={{flex: 1}}>
                <ConditionGroupCard
                  groupIndex={groupIndex}
                  parameters={parameters}
                  onRemove={() => remove(groupIndex)}
                  showRemove={fields.length > 1}
                />
              </Box>
            </Box>
          </React.Fragment>
        ))}
      </Box>

      <Box>
        <Button
          buttonType="outlined-primary"
          size="small"
          onClick={onAddGroup}
          disabled={parameters.length === 0}
          sx={{
            backgroundColor: Colors.bw.white,
            "&:hover": {backgroundColor: Colors.blue._100},
          }}
        >
          + Add condition group
        </Button>
      </Box>
    </Box>
  );
};

export default ConditionsCard;
